import React, { useEffect, useRef, useState } from "react";

// File -> dataURL (to match your PhotoUploadQuestion format)
const toDataURL = (file) =>
    new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
    });

const InsurancePhotoQuestion = ({ onSubmit, onNext, initialValue }) => {
    const [front, setFront] = useState(initialValue?.insurance?.cardFront ?? null);
    const [back, setBack] = useState(initialValue?.insurance?.cardBack ?? null);
    const [isUploadingFront, setIsUploadingFront] = useState(false);
    const [isUploadingBack, setIsUploadingBack] = useState(false);

    const frontInputRef = useRef(null);
    const backInputRef = useRef(null);

    useEffect(() => {
        if (!initialValue) return;
        const legacyFront =
            Array.isArray(initialValue?.cardFront) && initialValue.cardFront[0]?.data
                ? { dataURL: initialValue.cardFront[0].data, fileName: initialValue.cardFront[0].name || "" }
                : null;
        const legacyBack =
            Array.isArray(initialValue?.cardBack) && initialValue.cardBack[0]?.data
                ? { dataURL: initialValue.cardBack[0].data, fileName: initialValue.cardBack[0].name || "" }
                : null;

        setFront(initialValue?.insurance?.cardFront ?? legacyFront ?? front ?? null);
        setBack(initialValue?.insurance?.cardBack ?? legacyBack ?? back ?? null);
    }, [back, front, initialValue]);

    const handlePick = async (files, which) => {
        if (!files || !files.length) return;
        const file = files[0];
        const isFront = which === "front";

        isFront ? setIsUploadingFront(true) : setIsUploadingBack(true);

        try {
            const dataURL = await toDataURL(file);
            const entry = { dataURL, fileName: file.name };
            if (isFront) setFront(entry);
            else setBack(entry);
        } finally {
            if (isFront) {
                setIsUploadingFront(false);
                if (frontInputRef.current) frontInputRef.current.value = "";
            } else {
                setIsUploadingBack(false);
                if (backInputRef.current) backInputRef.current.value = "";
            }
        }
    };

    const removeFront = () => setFront(null);
    const removeBack = () => setBack(null);

    const canSubmit = !!front;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        const insurance = {
            memberId: initialValue?.insurance?.memberId ?? "",
            payerName: initialValue?.insurance?.payerName ?? "United Healthcare",
            relationship: initialValue?.insurance?.relationship ?? "Self",
            cardFront: front ? { dataURL: front.dataURL, fileName: front.fileName } : null,
            cardBack: back ? { dataURL: back.dataURL, fileName: back.fileName } : null,
        };

        onSubmit({ insurance });

        onNext();
    };

    return (
        <form onSubmit={handleSubmit} className="input-form">
            <div className="photo-upload-container">
                <div className="photo-upload-instructions">
                    <p>Upload your insurance card photos. Front is required; back is optional.</p>
                </div>

                {/* FRONT */}
                <div className="photo-upload-area" style={{ flexDirection: "column", alignItems: "center", gap: 12 }}>
                    <input
                        ref={frontInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePick(e.target.files, "front")}
                        style={{ display: "none" }}
                    />
                    <button
                        type="button"
                        className="photo-upload-button"
                        onClick={() => frontInputRef.current?.click()}
                        disabled={isUploadingFront}
                        aria-label="Upload insurance card front"
                        title="Upload insurance card front"
                    >
                        {isUploadingFront ? "Uploading front…" : front ? "Replace Front Photo" : "Upload Front (Required)"}
                    </button>

                    {front && (
                        <div className="photo-preview-container" style={{ width: "100%", maxWidth: 520 }}>
                            <h4>Front</h4>
                            <div className="photo-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                                <div className="photo-preview-item">
                                    <img src={front.dataURL} alt="Insurance card front" className="photo-preview" />
                                    <button
                                        type="button"
                                        className="photo-remove-button"
                                        onClick={removeFront}
                                        aria-label="Remove front photo"
                                        title="Remove"
                                    >
                                        x
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* BACK (optional) */}
                <div className="photo-upload-area" style={{ flexDirection: "column", alignItems: "center", gap: 12 }}>
                    <input
                        ref={backInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePick(e.target.files, "back")}
                        style={{ display: "none" }}
                    />
                    <button
                        type="button"
                        className="photo-upload-button"
                        onClick={() => backInputRef.current?.click()}
                        disabled={isUploadingBack}
                        aria-label="Upload insurance card back"
                        title="Upload insurance card back"
                    >
                        {isUploadingBack ? "Uploading back…" : back ? "Replace Back Photo" : "Upload Back (Optional)"}
                    </button>

                    {back && (
                        <div className="photo-preview-container" style={{ width: "100%", maxWidth: 520 }}>
                            <h4>Back</h4>
                            <div className="photo-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                                <div className="photo-preview-item">
                                    <img src={back.dataURL} alt="Insurance card back" className="photo-preview" />
                                    <button
                                        type="button"
                                        className="photo-remove-button"
                                        onClick={removeBack}
                                        aria-label="Remove back photo"
                                        title="Remove"
                                    >
                                        x
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <button type="submit" className="photo-submit-button" disabled={!canSubmit}>
                    Continue
                </button>
            </div>
        </form>
    );
};

export default InsurancePhotoQuestion;
